import qs from 'qs';
import constants, {
  BOARD_VIEW_PAGE_SIZE,
  ENTITY_FORMATS,
  GLOBAL_DEFAULT_PAGE_SIZE,
  PERMISSION_TYPES,
} from '../constants/CommonConstants';
import { convertFilterToVfl } from './velarisFilterLanguageUtils';
import { ENTITY_LEVELS_WITH_SAVABLE_FILTERS } from '../constants/FilteredViewConstatnts';
import { stringToBoolean } from './commonUtil';

const DEFAULT_FILTERS_BY_LEVEL = {
  [constants.ORGANIZATION_LEVEL]:
    'boolean::system:entityArchived=equals=(false);boolean::system:churned=equals=(false)',
  [constants.ACCOUNT_LEVEL]:
    'boolean::system:entityArchived=equals=(false);boolean::system:churned=equals=(false)',
  [constants.CONTACT_LEVEL]: 'boolean::system:entityArchived=equals=(false)',
  [constants.PIPELINE_LEVEL]: 'boolean::system:entityArchived=equals=(false)',
  [constants.CUSTOM_OBJECT_LEVEL]:
    'boolean::system:entityArchived=equals=(false)',
};
// eslint-disable-next-line import/prefer-default-export
export const filterOwnedEntities = (entityList, userId) => {
  return entityList.filter((entity) =>
    entity.owners?.some((owner) => owner.id === userId)
  );
};

export const isOwnedEntity = (entity, currentUser) => {
  if (currentUser.roleName === PERMISSION_TYPES.NORMAL_USER) {
    return entity.owners?.some((owner) => owner.id === currentUser.userId);
  }
  return true;
};

export const handleEntityFetchParams = (
  payload = {},
  displayCurrency,
  entityLevel
) => {
  const {
    pageSize,
    page,
    sortColumn,
    sortDirection,
    query,
    filters,
    isBoard,
    preservePreviousList,
    useDefaultFilterString,
    myEntitiesOnly,
    entityIds,
  } = payload;
  if (preservePreviousList && !isBoard) {
    throw new Error('Preserving list is only available for board views');
  }

  // Keep the value consistent for pagaination
  const defaultPageSize = isBoard
    ? BOARD_VIEW_PAGE_SIZE
    : GLOBAL_DEFAULT_PAGE_SIZE;

  const params = {
    page: page || 1,
    pageSize: pageSize || defaultPageSize,
    displayCurrency,
  };
  // Params obj contains mandatory params
  // Below are conditional params, only added if provided
  if (sortColumn && sortDirection) {
    params.sortColumn = sortColumn;
    params.sortDirection = sortDirection;
  }
  if (filters && filters.length > 0) {
    params.filter = convertFilterToVfl(filters);
  } else if (useDefaultFilterString) {
    params.filter = DEFAULT_FILTERS_BY_LEVEL[entityLevel];
  }
  if (query && query.length > 0) {
    // dont trim query: ' ' is vaid search?
    params.query = query;
  }
  if (isBoard) {
    params.format = ENTITY_FORMATS.BOARD;
  }
  if (payload.pipelineType) {
    params.pipelineType = payload.pipelineType;
  }
  if (myEntitiesOnly) {
    params.myEntitiesOnly = true;
  }
  let body = null;
  if (entityIds && Array.isArray(entityIds)) {
    body = {
      ids: entityIds.join(','),
    };
  }

  return [qs.stringify(params), body];
};

export function mergeMultipleResponses(responses) {
  return responses.reduce((finalResponse, pageResponse) => {
    if (!finalResponse) {
      return { ...pageResponse };
    }
    return {
      ...finalResponse,
      ...pageResponse,
      data: [...finalResponse.data, ...pageResponse.data],
      itemCount: finalResponse.itemCount + pageResponse.itemCount,
    };
  }, null);
}

export function formatMassUpdateCustomData(data, definitions) {
  const formattedData = {};
  Object.entries(data).forEach(([key, value]) => {
    const attribute = definitions[key];
    let _value;
    switch (attribute?.dataType) {
      case 'date':
        _value = value?.format('YYYY-MM-DD') || null;
        break;
      case 'number':
        _value = +value;
        break;
      case 'boolean':
        _value = stringToBoolean(value);
        break;
      case 'currency':
        if (value?.value) {
          _value = {
            currencyDefinitionId: +value.currencyDefinitionId,
            value: +value.value,
          };
        } else {
          _value = null;
        }
        break;
      default:
        _value = value;
        break;
    }
    formattedData[key] = _value;
  });
  return formattedData;
}

/**
 * Returns the entity status label(Churned or Archived) based on the given parameters
 * @param {*} churned
 * @param {*} entityArchived
 * @returns
 */
export function getEntityActivityStatus(churned, entityArchived) {
  if (entityArchived === 1 || (entityArchived === 1 && churned === 1)) {
    return 'Archived';
  }
  if (churned === 1) {
    return 'Churned';
  }
  return '';
}

export const populateEntityOptions = (entities) => {
  return entities.map((entity) => {
    const entityStatusLabel = getEntityActivityStatus(
      entity.churned || 0, // TODO: remove when BE sends churned as boolean
      entity.entityArchived || 0 // TODO: remove when BE sends entityArchived as boolean
    );

    return {
      displayName: entity?.globalFields?.name || entity.displayName,
      value:
        entity.organizationId ??
        entity.accountId ??
        entity.contactId ??
        entity.id ??
        entity.entityId ??
        entity.value,
      email: entity.email,
      imageId: entity.imageId,
      statusLabel: entityStatusLabel,
      churned: entity.churned || 0, // TODO: remove when BE sends churned as boolean
      entityArchived: entity.entityArchived, // TODO: remove when BE sends entityArchived as boolean
      owners: entity.owners,
    };
  });
};

/**
 * Function sorts entities based on the status in the alphabetical order and status
 * @param {*} entityList
 * @returns
 */
export function sortEntityByActivityStatus(entityList) {
  const entityInfo = [...entityList];
  return entityInfo.sort((a, b) => {
    return (
      a.entityArchived - b.entityArchived ||
      a.churned - b.churned ||
      (a.globalFields.name ?? 'undefined').localeCompare(
        b.globalFields.name ?? 'undefined'
      )
    );
  });
}

export const saturateUnAuthEntities = (
  filteredEntities,
  entityList,
  saturateUnauthorized
) => {
  const unAuthorisedEntities = entityList.filter(
    (entity) =>
      !filteredEntities.some(
        (filteredEntity) => filteredEntity.entityId === entity.entityId
      )
  );
  return unAuthorisedEntities
    .filter(
      (unAuthEntity) => saturateUnauthorized.indexOf(unAuthEntity.entityId) > -1
    )
    .map((entity) => ({ ...entity, unOwned: true }));
};

export default function isEntityLevelWithSavableFilters(
  entityType,
  nonSystemCustomObjects
) {
  return (
    ENTITY_LEVELS_WITH_SAVABLE_FILTERS.includes(entityType) ||
    !!nonSystemCustomObjects.find(({ apiName }) => apiName === entityType)
  );
}
